import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { API_URL } from '../config';
import { fetchApi, authedFetch } from '../utils/apiUtils';
import { ConfirmDialog } from './common/ConfirmDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

interface Region {
  region_id: number;
  region_code: string;
  region_name: string;
  region_description: string;
  is_active: boolean;
}

interface RegionFormData {
  region_id?: number;
  region_code: string;
  region_name: string;
  region_description: string;
  is_active: boolean;
}

const emptyRegion: RegionFormData = {
  region_code: '',
  region_name: '',
  region_description: '',
  is_active: true,
};

function RegionManagement(): React.ReactElement {
  const [regions, setRegions] = useState<Region[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRegion, setEditingRegion] = useState<RegionFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Region | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async (): Promise<void> => {
    try {
      const data = (await fetchApi(`${API_URL}/regions`)) as Region[] | { error: string };
      if ('error' in data) {
        setError(data.error);
        return;
      }
      setRegions(data);
    } catch (err) {
      console.error('Error fetching regions:', err);
      setError('Failed to fetch regions');
    }
  };

  const stats = useMemo(() => {
    const total = regions.length;
    const active = regions.filter((r) => r.is_active).length;
    const inactive = total - active;
    return [
      { label: 'Total Regions', value: total.toLocaleString(), icon: <PublicIcon /> },
      { label: 'Active', value: active.toLocaleString(), icon: <CheckCircleIcon />, tone: 'success' as const },
      { label: 'Inactive', value: inactive.toLocaleString(), icon: <RemoveCircleOutlineIcon /> },
    ];
  }, [regions]);

  const filteredRegions = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return regions;
    return regions.filter(
      (r) =>
        (r.region_name ?? '').toLowerCase().includes(q) ||
        (r.region_code ?? '').toLowerCase().includes(q) ||
        (r.region_description ?? '').toLowerCase().includes(q),
    );
  }, [regions, searchQuery]);

  const validateRegion = (region: RegionFormData): string | null => {
    if (!region.region_code) return 'Region code is required';
    if (!region.region_name) return 'Region name is required';
    return null;
  };

  const handleOpenAdd = (): void => {
    setEditingRegion({ ...emptyRegion });
    setDialogError(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (region: Region): void => {
    setEditingRegion({ ...region });
    setDialogError(null);
    setOpenDialog(true);
  };

  const handleSaveRegion = async (): Promise<void> => {
    if (!editingRegion) return;
    const validationError = validateRegion(editingRegion);
    if (validationError) {
      setDialogError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const isEdit = Boolean(editingRegion.region_id);
      const url = isEdit
        ? `${API_URL}/regions/${editingRegion.region_id}`
        : `${API_URL}/regions`;
      const response = await authedFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingRegion),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setDialogError(data.error || 'Failed to save region');
        return;
      }
      setRegions((prev) =>
        isEdit ? prev.map((r) => (r.region_id === data.region_id ? data : r)) : [...prev, data],
      );
      setEditingRegion(null);
      setOpenDialog(false);
    } catch (err) {
      console.error('Error saving region:', err);
      setDialogError('Failed to save region');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDeleteRegion = async (): Promise<void> => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const response = await authedFetch(`${API_URL}/regions/${deleteTarget.region_id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to delete region');
        return;
      }
      setRegions((prev) => prev.filter((r) => r.region_id !== deleteTarget.region_id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting region:', err);
      setError('Failed to delete region');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'region_code',
      headerName: 'Code',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value as string}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.7rem', letterSpacing: '0.04em' }}
        />
      ),
    },
    {
      field: 'region_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          {params.value as string}
        </Typography>
      ),
    },
    {
      field: 'region_description',
      headerName: 'Description',
      flex: 2,
      minWidth: 240,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {(params.value as string) || ''}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpenEdit(params.row as Region)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => setDeleteTarget(params.row as Region)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'error.main' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          Regions
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add Region
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={12} sm={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box
                    sx={{
                      color: stat.tone === 'success' ? 'success.main' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search regions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: '100%', maxWidth: 360 }}
        />
      </Paper>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={filteredRegions}
          columns={columns}
          getRowId={(row) => row.region_id}
          autoHeight
          disableSelectionOnClick
          sx={{
            border: 'none',
            fontFamily: 'inherit',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
              borderBottom: '1px solid',
              borderColor: 'divider',
              minHeight: '40px !important',
              maxHeight: '40px !important',
              lineHeight: '40px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '0 12px',
              '&:focus, &:focus-within': { outline: 'none' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
            },
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
            '& .MuiDataGrid-cell': {
              padding: '0 12px',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderBottom: '1px solid',
              borderColor: 'grey.100',
              '&:focus, &:focus-within': { outline: 'none' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
            },
            '& .MuiDataGrid-row': {
              transition: 'background-color 120ms',
              '&:hover': { backgroundColor: 'action.hover' },
            },
            '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: '44px',
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.8125rem',
            },
          }}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={() => {
          if (isSaving) return;
          setOpenDialog(false);
          setEditingRegion(null);
          setDialogError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingRegion?.region_id ? 'Edit Region' : 'Add Region'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label="Code"
              fullWidth
              required
              value={editingRegion?.region_code ?? ''}
              onChange={(e) =>
                setEditingRegion((prev) => prev && { ...prev, region_code: e.target.value })
              }
            />
            <TextField
              size="small"
              label="Name"
              fullWidth
              required
              value={editingRegion?.region_name ?? ''}
              onChange={(e) =>
                setEditingRegion((prev) => prev && { ...prev, region_name: e.target.value })
              }
            />
            <TextField
              size="small"
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editingRegion?.region_description ?? ''}
              onChange={(e) =>
                setEditingRegion(
                  (prev) => prev && { ...prev, region_description: e.target.value },
                )
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingRegion?.is_active ?? true}
                  onChange={(e) =>
                    setEditingRegion(
                      (prev) => prev && { ...prev, is_active: e.target.checked },
                    )
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={isSaving}
            onClick={() => {
              setOpenDialog(false);
              setEditingRegion(null);
              setDialogError(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveRegion} variant="contained" disabled={isSaving}>
            {isSaving ? 'Saving' : editingRegion?.region_id ? 'Save changes' : 'Add region'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Region"
        message={
          deleteTarget
            ? `Delete ${deleteTarget.region_name}? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        loading={isDeleting}
        onConfirm={confirmDeleteRegion}
        onCancel={() => setDeleteTarget(null)}
      />

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RegionManagement;
